// M4 workstream A (shale) — tick hooks and the small entry points the shared core files call (marked `// M4-A:` there).
// Everything is inert before 2000: the ticks return at once until state.shale exists (created lazily by the first
// shale pad / service / well, always ≥ 2000), and the hooks key on well.lateral / prod.shale which only exist from 2000.
import { M4_FROM, modHypRate } from '../../data/m4/rules.js';
import { yf, eqDef, fieldDef, isAvailable } from '../defs.js';
import { createWell } from '../drilling.js';
import { addStock } from '../../core/state.js';
import { spend, fmt$ } from '../util.js';
import { sh, benchesAt, padKey, ok, no } from './shaleUtil.js';
import { tickServices, tickWater, contractFracServiceImpl, buildWaterFacilityImpl, contractWaterServiceImpl, sandDeliveredPerTon } from './shaleServices.js';
import { tickFrac, tickDucs, fracWellImpl, normalizeDesign, ip30For } from './shaleFrac.js';
import { tickShaleWells, startShaleProduction } from './shaleProd.js';
import { lateralGeometry } from './shaleLaterals.js';

export function tickPre(state) {
  if (!state.shale || yf(state) < M4_FROM) return;
  tickServices(state);
  tickFrac(state);
}
export function tickPost(state) {
  if (!state.shale || yf(state) < M4_FROM) return;
  tickWater(state);
  tickShaleWells(state);
}
export function tickEcon(state) {
  if (!state.shale || yf(state) < M4_FROM) return;
  tickDucs(state);
}

// ---- hooks for shared files -------------------------------------------------------------------------------------
export { onReachKop, onLateralPay, lateralSidetrack, shaleRopMult, isLateralPhase, lateralDay, shaleMpdGain } from './shaleLaterals.js';
export { shaleRate, shaleProduced, shaleVerticalQiMult, shaleWellStreams } from './shaleProd.js';
export { shalePadSlot, shaleOnAssign } from './shalePads.js';
export { shaleLateralView } from './shaleViews.js';

// completeImpl on a lateral well: the "completion" is the frac job.
export function shaleComplete(state, well) {
  if (well.lateral.phase !== 'done') return no('Finish drilling the lateral first.');
  return fracWellImpl(state, well);
}

// buyEquipment for the new shop tabs: completion services are contracted to a field, proppant bought by the ton at its
// delivered price, water facilities built or contracted at a field. Returns null to fall through to the default.
export function shaleBuyRoute(state, def, qty, opts = {}) {
  if (yf(state) < M4_FROM) return null;
  const fid = opts.fieldId ?? null;
  if (def.cat === 'completion') {
    const kind = def.stats?.service;
    if (kind === 'proppant') {
      const tons = Math.max(1, Math.round(qty));
      const at = fid ?? Object.keys(state.fields).find((f) => fieldDef(f)?.shale && Object.values(state.wells).some((w) => w.fieldId === f)) ?? null;
      const per = at ? sandDeliveredPerTon(state, def.id, at) : null;
      if (per == null) return null;
      if (!spend(state, per * tons, 'consumables')) return no(`${tons.toLocaleString('en-US')} t of ${def.name} costs ${fmt$(per * tons)}; not enough cash.`);
      addStock(state, def.id, tons);
      return ok(`Bought ${tons.toLocaleString('en-US')} t of ${def.name} delivered to ${fieldDef(at)?.name} (${fmt$(per)}/t).`);
    }
    if (!fid) return no('Choose the field the crew will work (completion services are contracted to a field).');
    return contractFracServiceImpl(state, def.id, { fieldId: fid, days: opts.days ?? null });
  }
  if (def.cat === 'water') {
    if (!fid) return no('Choose the field for the water facility.');
    return def.basePrice > 0 ? buildWaterFacilityImpl(state, def.id, fid) : contractWaterServiceImpl(state, def.id, fid, { days: opts.days ?? null });
  }
  return null;
}

// ---- Quick Start helper (workstream C builds QS-2000/2014 with it) --------------------------------------------------
// Builds a finished horizontal well on a shale plot: a DUC (duc: true) or a producing well `ageDays` old.
// opts: { fieldId, plot, bench?, lengthFt, inZoneFrac = 0.85, ageDays = 0, duc = false, design?, name? } → well
export function shaleQuickstartWell(state, opts) {
  const { fieldId, plot } = opts;
  const fdef = fieldDef(fieldId);
  const p = state.fields[fieldId]?.plots?.[plot];
  if (!fdef?.shale || !p) return null;
  const benches = benchesAt(fdef, p);
  const bench = benches.find((b) => b.id === opts.bench) ?? benches[0];
  if (!bench) return null;
  const rigDefId = eqDef('super_spec_pad') && isAvailable(eqDef('super_spec_pad'), state) ? 'super_spec_pad' : 'ac_drive_land';
  const well = createWell(state, { uid: null, defId: rigDefId, crewIds: [], bitUid: null }, fieldId, plot, 1000);
  if (opts.name) well.name = opts.name;
  well.rigId = null; well.rigType = 'rotary';
  const g = lateralGeometry(state, well, bench, 'rss');
  const len = Math.round(opts.lengthFt ?? 7500);
  const iz = opts.inZoneFrac ?? 0.85;
  const S = sh(state);
  const key = padKey(fieldId, plot);
  const pad = (S.pads[key] ??= { uid: key, fieldId, plot, builtDay: state.day - (opts.ageDays ?? 0), readyDay: state.day - (opts.ageDays ?? 0), wellIds: [], cost: 0 });
  pad.wellIds.push(well.id);
  well.lateral = { bench: bench.id, benchName: bench.bench ?? bench.name, windowFt: bench.windowFt ?? 50, thick: bench.thick, kickoffMd: g.kickoffMd, landingTvd: g.landingTvd, windowTop: g.windowTop,
    curveMd: g.curveMd, bur: g.bur, tool: 'rss', targetFt: len, lengthFt: len, curveDrilled: g.curveMd, inZoneFt: len * iz, inZoneFrac: iz, phase: 'done', steer: 'auto', pos: 0, dipSign: 1,
    gamma: [], stuckEvents: 0, sidetracks: 0, padUid: key, spacingFt: null, plannedDay: state.day, verticalTarget: g.landingTvd, stages: null, proppantLbPerFt: null, stageSpacingFt: null,
    fluid: null, completedDay: null, parentOf: [], childOf: [], design: null, drilledDay: state.day - (opts.ageDays ?? 0) };
  well.depth = g.landingTvd; well.target = g.landingTvd; well.md = g.kickoffMd + g.curveMd + len;
  well.spudDay = state.day - (opts.ageDays ?? 0) - 25; well.reservoirId = bench.id; well.payTop = bench.top; well.payFt = bench.thick; well.revealed.push(bench.id);
  well.strikeDay = well.spudDay + 20;
  if (opts.duc) { well.status = 'AT_TD'; well.duc = { since: state.day - (opts.ageDays ?? 0) }; return well; }
  const d = normalizeDesign(state, well, opts.design ?? {});
  well.lateral.design = d;
  const ip = ip30For(state, well, d, { childMult: 1 });
  startShaleProduction(state, well, ip);
  const L = well.lateral;
  L.stages = Math.ceil(len / d.stageSpacingFt); L.proppantLbPerFt = d.proppantLbPerFt; L.stageSpacingFt = d.stageSpacingFt; L.fluid = d.fluid; L.isolation = d.isolation; L.completedDay = state.day - (opts.ageDays ?? 0);
  const pr = well.prod;
  const age = Math.max(0, Math.round(opts.ageDays ?? 0));
  let cum = 0;
  for (let t = 0; t < age; t += 30) cum += modHypRate(pr.qi, pr.di, pr.b, pr.dmin, (t + 15) / 365) * Math.min(30, age - t);
  pr.age = age; pr.startDay = state.day - age; pr.cum = Math.round(cum);
  if (age >= pr.flowDays) { const id = ['rod_lift_long_stroke', 'esp_high_rate', 'gas_lift_hp', 'plunger_lift'].find((x) => eqDef(x) && isAvailable(eqDef(x), state)); if (id) { well.lift = { defId: id, uid: null, usesRig: false, share: 1 }; pr.liftDay = state.day; } }
  S.pools[`${fieldId}:${plot}:${bench.id}`] = (S.pools[`${fieldId}:${plot}:${bench.id}`] ?? 0) + pr.cum;
  return well;
}
